import { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { Panel } from '../../components/panels/Panel';
import { getInfluxdbData } from '../../services/api/influxdb-tools';
import { DEFECT_TYPES_NAMES } from '../../services/live-interface/verallia-interface-service';
import { getVeralliaDashboardInfluxDetections } from '../../services/dashboard/verallia-dashboard-influx-service';
import { getVeralliaDashboardPeriodRange } from '../../services/dashboard/verallia-dashboard-service';
import { EvaluationPage } from './evaluation/EvaluationPage';
import { MonitoringPage } from './monitoring/MonitoringPage';
import { VeralliaDashboardDetections } from './verallia-components/VeralliaDashboardDetections';
import { VeralliaDashboardLastShiftReport } from './verallia-components/VeralliaDashboardLastShiftReport';
import { VeralliaDashboardStatistics } from './verallia-components/VeralliaDashboardStatistics';
import { VeralliaDashboardDefectCatalogue } from './verallia-components/VeralliaDashboardDefectCatalogue';
import { useUser } from "../../contexts/UserContext";

const MENU_ITEMS = [
  { label: 'Detecciones', path: 'detections' },
  { label: 'Informe del último equipo', path: 'last-shift-report' },
  { label: 'Estadísticas', path: 'statistics' },
  { label: 'Catálogo de defectos', path: 'defect-catalogue' },
  { label: 'Evaluación', path: 'evaluation', separator: true },
  { label: 'Supervisión', path: 'monitoring', separator: true },
];

const STORAGE_KEY = 'verallia_dashboard_preferences_v1';
const RANGE_KEYS = ['last_24h', 'last_7d', 'last_30d', 'all', 'custom'];

const normalizeFilters = (filters) => {
  const rangeKey = RANGE_KEYS.includes(filters?.rangeKey) ? filters.rangeKey : 'last_24h';
  const next = { lines: Array.isArray(filters?.lines) ? filters.lines : [], defects: Array.isArray(filters?.defects) ? filters.defects : [], rangeKey };
  if (rangeKey === 'custom' && Number.isFinite(filters?.start) && Number.isFinite(filters?.end) && filters.start <= filters.end) {
    next.start = filters.start;
    next.end = filters.end;
  } else if (rangeKey === 'custom') next.rangeKey = 'last_24h';
  return next;
};

const loadDashboardPreferences = () => {
  const defaults = { tab: 'detections', filters: normalizeFilters(null), detectionsPage: 1 };
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    if (!saved || typeof saved !== 'object') return defaults;
    return {
      tab: MENU_ITEMS.some(({ path }) => path === saved.tab) ? saved.tab : defaults.tab,
      filters: normalizeFilters(saved.filters),
      detectionsPage: Number.isInteger(saved.detectionsPage) && saved.detectionsPage > 0 ? saved.detectionsPage : 1,
    };
  } catch { return defaults; }
};

const DEFECT_OPTIONS = Object.entries(DEFECT_TYPES_NAMES).map(([id, name]) => ({ id: Number(id), name }));

const loadEvaluationCandidateIds = async (defectType) => {
  if (!Number.isInteger(defectType) || defectType < 0) return [];

  const query = `from(bucket: "engine")
  |> range(start: 0)
  |> filter(fn: (r) => r._measurement == "pallet_defect" and r._field == "detected")
  |> filter(fn: (r) => r.defect_type == "${defectType}" and r.client_id =~ /^verallia($|_)/)
  |> keep(columns: ["defect_id", "_time", "camera_id", "camera", "camera_ip", "ip", "linea", "line", "client_id"])
  |> group(columns: ["defect_id"])
  |> first(column: "_time")`;

  const rows = await getInfluxdbData(null, null, [], [], null, null, null, null, null, [], null, query);
  return rows.map((row) => {
    const parsedTime = row._time ? Date.parse(row._time) : NaN;
    const line = String(row.line ?? '').match(/^line_(\d+)$/)?.[1] ?? null;
    const camera = String(row.client_id ?? '').match(/^verallia_(\d+)$/)?.[1] ?? null;
    return { id: String(row.defect_id ?? ''), timestamp: Number.isFinite(parsedTime) ? Math.floor(parsedTime / 1000) : null, camera, line };
  }).filter(({ id }) => id !== '');
};

const getEvaluationImageUrl = (defectId) => `${process.env.REACT_APP_FILE_URL}defects/${encodeURIComponent(defectId)}.jpg`;

export const VeralliaDashboard = () => {
  const location = useLocation();
  const [preferences, setPreferences] = useState(loadDashboardPreferences);
  const [detections, setDetections] = useState([]);
  const [isInfluxLoading, setIsInfluxLoading] = useState(true);
  const [hasInfluxError, setHasInfluxError] = useState(false);
  const { user } = useUser();

  const filters = useMemo(() => {
    const selected = preferences.filters;
    const period = selected.rangeKey === 'custom'
      ? { start: selected.start, end: selected.end, rangeKey: 'custom' }
      : getVeralliaDashboardPeriodRange(selected.rangeKey, detections);
    return { ...period, lines: selected.lines, defects: selected.defects };
  }, [preferences.filters, detections]);

  const handleFiltersChange = useCallback((nextFilters) => {
    setPreferences((current) => ({ ...current, filters: normalizeFilters(nextFilters), detectionsPage: 1 }));
  }, []);

  const handleDetectionsPageChange = useCallback((page) => {
    setPreferences((current) => ({ ...current, detectionsPage: page }));
  }, []);

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences)); }
    catch (error) { console.warn('Unable to save dashboard preferences.', error); }
  }, [preferences]);

  useEffect(() => {
    const tab = location.pathname.split('/').filter(Boolean).pop();
    if (!MENU_ITEMS.some(({ path }) => path === tab)) return;
    setPreferences((current) => current.tab === tab ? current : { ...current, tab });
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;

    const getData = async () => {
      setIsInfluxLoading(true);
      setHasInfluxError(false);

      try {
        const nextDetections = await getVeralliaDashboardInfluxDetections();
        if (!cancelled) setDetections(nextDetections);
      } catch (error) {
        if (cancelled) return;
        console.error('Unable to load Verallia dashboard data.', error);
        setDetections([]);
        setHasInfluxError(true);
      } finally {
        if (!cancelled) setIsInfluxLoading(false);
      }
    };

    getData();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="flex h-full bg-perception-black-900">
      <Panel noHeader noPadding category="main" width="w-60" overflowAuto={false} className="shrink-0 border-r border-perception-black-500">
        <div className="flex h-full flex-col p-3">
          <div className="px-3 py-4 text-xs font-semibold uppercase tracking-wider text-perception-gray-600">Panel de control</div>

          <div className="flex flex-col gap-1">
            {MENU_ITEMS
              .filter(({ path }) => path !== 'evaluation' || user?.roles?.includes('ROLE_ADMIN'))
              .map(({ label, path, separator }) => (
              <div key={path} className={separator ? 'mt-3 border-t border-perception-black-500 pt-3' : ''}>
                <NavLink to={path} className={({ isActive }) => clsx('block rounded-md px-3 py-2 text-sm transition-colors', isActive ? 'bg-perception-black-500 text-white' : 'text-perception-gray-400 hover:bg-perception-black-700 hover:text-white')}>
                  {label}
                </NavLink>
              </div>
            ))}
          </div>
        </div>
      </Panel>

      <div className="min-w-0 flex-1">
        <Routes>
          <Route index element={<Navigate to={preferences.tab} replace/>}/>
          <Route path="detections" element={<VeralliaDashboardDetections detections={detections} isInfluxLoading={isInfluxLoading} hasInfluxError={hasInfluxError} filters={filters} onFiltersChange={handleFiltersChange} page={preferences.detectionsPage} onPageChange={handleDetectionsPageChange}/>}/>
          <Route path="last-shift-report" element={<VeralliaDashboardLastShiftReport detections={detections} isInfluxLoading={isInfluxLoading} hasInfluxError={hasInfluxError}/>}/>
          <Route path="statistics" element={<VeralliaDashboardStatistics detections={detections} isInfluxLoading={isInfluxLoading} hasInfluxError={hasInfluxError} filters={filters} onFiltersChange={handleFiltersChange} onDetectionsPageChange={handleDetectionsPageChange}/>}/>
          <Route path="defect-catalogue" element={<VeralliaDashboardDefectCatalogue detections={detections} isInfluxLoading={isInfluxLoading} hasInfluxError={hasInfluxError}/>}/>
          <Route path="evaluation" element={<EvaluationPage clientId="verallia" defectOptions={DEFECT_OPTIONS} loadCandidateIds={loadEvaluationCandidateIds} getImageUrl={getEvaluationImageUrl} timeZone="Europe/Paris"/>}/>
          <Route path="monitoring" element={<MonitoringPage/>}/>
          <Route path="*" element={<Navigate to={preferences.tab} replace/>}/>
        </Routes>
      </div>
    </div>
  );
};
